import {distinctUntilChanged, switchMap, take, tap} from 'rxjs';
import {get} from 'svelte/store';
import {buyCryptoCardApi} from '@/api/buy-crypto-card';
import {lang$} from '@/store/lang';
import {div, minus, plus} from '@/helper/num';
import {isLogin$} from '@/streams/auth';
import {mc} from '@/core';
import {trackQuotePricingError} from './track';

export const service_types = ['one_key_trade_banner', 'b2c_banner', 'fiat_deposit_banner', 'fiat_withdraw_banner'] as const;
export const service_directions = ['buy', 'sell'] as const;
export const paybis_payment_methods = ['INSTANT_ACH', 'PIX', 'PAYBIS_THIRD'] as const;
export const google_apple_apy_methods = ['GOOGLE_APPLE_PAY'];
export const payment_methods = ['FIAT_BALANCE_V2', 'THIRD_PARTY', 'CREDIT_DEBIT_CARDS', 'CREDIT_DEBIT_CARDS_V3', 'GOOGLE_PAY', 'APPLE_PAY', ...paybis_payment_methods] as const;
export const quote_types = ['fiat', 'digital'] as const;

const loginStatus$ = isLogin$.pipe(distinctUntilChanged());
/**
 * 获取渠道配置数据
 */
export function createGetChannelConfigsObservable(params: GetChannelConfigsParams) {
  const types = (params?.types || []).join(',');
  return loginStatus$.pipe(
    take(1),
    switchMap((isLogin) => buyCryptoCardApi.getConfigs({...params, types}, isLogin)),
    tap((response) => {
      const {error} = response;
      if (error) {
        mc.send('toast', 'error', error);
      }
    })
  );
}

/**
 * 获取一个buy时候的绝对有效报价金额
 */
export function getSafeBuyAmount(channelConfig) {
  const min = channelConfig?.minBuyFiatAmount || '0';
  const max = channelConfig?.maxBuyFiatAmount || '0';
  const averageValue = div(minus(max, min), 2);
  const safeFiatAmount = plus(min, averageValue);
  return safeFiatAmount;
}

/**
 * 获取一个sell时候的绝对有效报价金额
 */
export function getSafeSellAmount(channelConfig) {
  const min = channelConfig.minSellDigitalAmount || '0';
  const max = channelConfig?.maxSellDigitalAmount || '0';
  const averageValue = div(minus(max, min), 2);
  const digitalAmount = plus(min, averageValue);
  return digitalAmount;
}

/** 获取报价数据 */
export function createGetPricingObservable(params: GetPricingParams) {
  const types = (params?.types || []).join(',');
  const requestParams = {...params, types};

  return loginStatus$.pipe(
    take(1),
    switchMap((isLogin) => buyCryptoCardApi.getPricing(requestParams, isLogin)),
    tap((response) => {
      const {error} = response;
      if (!response?.data?.channels?.length) {
        trackQuotePricingError(requestParams);
      }
      if (error) {
        mc.send('toast', 'error', error);
      }
    })
  );
}

/** 查询bannerList数据  */
export function createGetBannerListObservable(type: SERVICE_TYPES) {
  const locationTypeMaps = {
    [service_types[1]]: 16,
    [service_types[0]]: 17,
    [service_types[2]]: 18,
    [service_types[3]]: 19,
  };
  const params = {
    itemKey: 'banner',
    lang: get(lang$),
    location: locationTypeMaps[type],
    device: 'WEB',
  };
  return loginStatus$.pipe(
    take(1),
    switchMap((isLogin) => buyCryptoCardApi.getAdBannerList(params, isLogin)),
    tap((response) => {
      const {error} = response;
      if (error) {
        mc.send('toast', 'error', error);
      }
    })
  );
}

export function safeGetServiceType(serviceType): SERVICE_TYPES {
  return service_types.includes(serviceType) ? serviceType : 'b2c_banner';
}

export function safeGetServiceDirection(serviceDirection): SERVICE_DIRECTIONS {
  return service_directions.includes(serviceDirection) ? serviceDirection : 'buy';
}

export type SERVICE_TYPES = typeof service_types[number];
export type SERVICE_DIRECTIONS = typeof service_directions[number];
export type PAYMENT_METHODS = typeof payment_methods[number];
export type QUOTE_TYPES = typeof quote_types[number];

export interface GetChannelConfigsParams {
  side: SERVICE_DIRECTIONS;
  types: Array<PAYMENT_METHODS>;
  channelName?: string;
  fiat?: string;
  digitalCurrency?: string;
}

export interface GetPricingParams {
  types: Array<string>;
  side: SERVICE_DIRECTIONS;
  fiatCurrency: string;
  fiatAmount?: number | string;
  digitalCurrency: string;
  digitalAmount?: number | string;
  chainName?: string;
  paymentWay?: string;
  bankType?: 'bank' | 'card';
  /** 卡片id，用于识别手续费类型，如果为空，按照无指定绑卡处理 */
  cardId?: number | string;
  channelName?: string;
}
